import { Fragment, useEffect, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { supabase, type UserProfile } from '../../../lib/supabase';
import { Link, useNavigate } from 'react-router-dom';
import { Package, MapPin, Briefcase, LogOut } from 'lucide-react';

export function UserProfileMenu() {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Récupérer l'utilisateur actuel
    const getUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setUser(session.user);
          
          // Récupérer le profil utilisateur
          const { data: profileData, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          if (!error && profileData) {
            setProfile(profileData);
          }
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        setLoading(false);
      }
    };

    getUser();

    // Écouter les changements de session
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setUser(session.user);
        // Récupérer le profil
        supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single()
          .then(({ data, error }) => {
            if (!error && data) {
              setProfile(data);
            }
          });
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return null;
  }

  if (!user) {
    return null;
  }

  const firstName = profile?.first_name || user.email?.split('@')[0] || 'Utilisateur';
  const avatarUrl = profile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(firstName)}&background=ff0442&color=fff&size=128`;

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="group relative inline-flex items-center gap-2 rounded-xl bg-white/10 backdrop-blur-sm px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-white/30 transition-all duration-200 hover:bg-white/20 hover:shadow-md hover:ring-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-[#ff0442]">
          <img
            src={avatarUrl}
            alt={firstName}
            className="h-8 w-8 rounded-full object-cover ring-2 ring-white/30"
          />
          <span className="hidden font-medium sm:inline">{firstName}</span>
          <ChevronDownIcon className="h-4 w-4 text-white/80 transition-transform duration-200 group-hover:text-white ui-open:rotate-180" aria-hidden="true" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95 translate-y-1"
        enterTo="transform opacity-100 scale-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="transform opacity-100 scale-100 translate-y-0"
        leaveTo="transform opacity-0 scale-95 translate-y-1"
      >
        <Menu.Items className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-2xl bg-white/95 backdrop-blur-xl shadow-xl ring-1 ring-neutral-200/50 focus:outline-none dark:bg-neutral-800/95 dark:ring-neutral-700/50">
          <div className="p-2">
            {/* Header avec profil */}
            <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
              <div className="flex items-center gap-3">
                <img
                  src={avatarUrl}
                  alt={firstName}
                  className="h-10 w-10 rounded-full object-cover ring-2 ring-neutral-200 dark:ring-neutral-700"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                    {firstName} {profile?.last_name || ''}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                    {user.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Menu items */}
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/mes-commandes"
                    className={`group relative flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-all duration-200 ${
                      active
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20'
                        : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
                    }`}
                  >
                    <Package className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                    <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                      Mes commandes
                    </span>
                  </Link>
                )}
              </Menu.Item>

              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/mon-adresse"
                    className={`group relative flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-all duration-200 ${
                      active
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20'
                        : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
                    }`}
                  >
                    <MapPin className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                    <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                      Mon adresse
                    </span>
                  </Link>
                )}
              </Menu.Item>

              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/espace-business"
                    className={`group relative flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-all duration-200 ${
                      active
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20'
                        : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
                    }`}
                  >
                    <Briefcase className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                    <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                      Espace Business
                    </span>
                  </Link>
                )}
              </Menu.Item>
            </div>

            {/* Séparateur */}
            <div className="border-t border-neutral-200 dark:border-neutral-700 my-1" />

            {/* Déconnexion */}
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={handleSignOut}
                  className={`group relative flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-all duration-200 ${
                    active
                      ? 'bg-red-50 dark:bg-red-900/20'
                      : 'hover:bg-red-50 dark:hover:bg-red-900/20'
                  }`}
                >
                  <LogOut className="h-5 w-5 text-red-600 dark:text-red-400" />
                  <span className="text-sm font-medium text-red-600 dark:text-red-400">
                    Se déconnecter
                  </span>
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

